/**
 * Shared visibility + ranking helpers so destination/city pages display
 * listings the same way SearchPage does.
 *
 * Mirrors the logic in:
 *   - src/containers/SearchPage/SearchPage.duck.js (resultIds visibility filter)
 *   - src/containers/SearchPage/SearchResultsPanel/SearchResultsPanel.js (dedup + tier rank)
 *
 * If you change one, update the others.
 */

// A listing is "visible" if it isn't explicitly hidden. publicData.WR === false
// means the host (or admin) flagged it to hide from search.
export const isVisibleListing = l => {
  const wr = l?.attributes?.publicData?.WR;
  return wr === true || wr === undefined || wr === null;
};

/**
 * Dedupe location variants and apply the promoted / partner / top-host
 * tier ranking used on the search grid.
 */
export const dedupeAndRankListings = (listings, { userSort } = {}) => {
  // Sort variants to the end first so the canonical/main listing wins during dedup.
  const sorted = [...listings].sort((a, b) => {
    const aIsVariant = a?.attributes?.publicData?.isLocationVariant === true ? 1 : 0;
    const bIsVariant = b?.attributes?.publicData?.isLocationVariant === true ? 1 : 0;
    return aIsVariant - bIsVariant;
  });

  const seenCanonicalIds = new Set();
  const seenTitleAuthor = new Set();
  const deduped = sorted.filter(l => {
    const pd = l?.attributes?.publicData || {};
    const isVariant = pd.isLocationVariant === true;
    const canonicalId = isVariant ? pd.mainListingId : l?.id?.uuid;
    if (canonicalId) {
      if (seenCanonicalIds.has(canonicalId)) return false;
      seenCanonicalIds.add(canonicalId);
    }
    const title = (l?.attributes?.title || '').trim().toLowerCase();
    const authorId =
      l?.author?.id?.uuid || l?.relationships?.author?.data?.id?.uuid || '';
    if (title && authorId) {
      const key = `${authorId}::${title}`;
      if (seenTitleAuthor.has(key)) return false;
      seenTitleAuthor.add(key);
    }
    return true;
  });

  // Only re-rank when no explicit user sort is in play (matches SearchPage).
  const isDefaultSort = !userSort || userSort === '' || userSort === 'relevance';
  if (!isDefaultSort) {
    return deduped;
  }

  const tierFor = l => {
    const pd = l?.attributes?.publicData || {};
    if (pd.promoteCommission === true) return 0;
    if (pd.isRentalTideListing === true || pd.partner === true) return 1;
    const hostBookings =
      Number(l?.author?.attributes?.profile?.metadata?.hostBookingCount) || 0;
    if (hostBookings >= 20) return 2;
    return 3;
  };

  return deduped
    .map((l, originalIndex) => ({ l, tier: tierFor(l), originalIndex }))
    .sort((a, b) => a.tier - b.tier || a.originalIndex - b.originalIndex)
    .map(x => x.l);
};
